/* Native document navigation with one real frame. No copied DOM or cached
   frame markup is ever painted over the incoming document. */
(() => {
  const root = document.documentElement;
  const key = 'ak-frame-navigation-v1';
  const navigationType = performance.getEntriesByType('navigation')[0]?.type;
  const current = new URL(location.href);
  const route = value => {
    try {
      const url = new URL(value, location.href);
      return url.origin === current.origin &&
        !/^\/(?:cart|checkout|checkouts|account|challenge|apps)(?:\/|$)/.test(url.pathname) ? url : null;
    } catch { return null; }
  };
  const samePage = (a, b) => a.pathname === b.pathname && a.search === b.search;
  let pending = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(key));
    sessionStorage.removeItem(key);
  } catch {}
  const from = route(window.navigation?.activation?.from?.url || document.referrer);
  const expected = pending && route(pending.to);
  const replay = navigationType === 'reload' && pending?.replay === true && expected && samePage(expected,current) && Date.now() - pending.at < 30000;
  // The destination record is written by this origin before following a link.
  // Safari may shorten or omit Referrer; it must not decide frame continuity.
  const recordedNavigation = pending && route(pending.from) && expected && samePage(expected,current) &&
    Date.now() - pending.at >= 0 && Date.now() - pending.at < 30000;
  const internal = replay || navigationType !== 'reload' && Boolean(
    recordedNavigation || navigationType === 'back_forward' && from
  );
  root.classList.toggle('ak-internal-navigation', internal);
  // Only the stationary frame skips its entrance. Content always starts fresh.
  root.classList.remove('ak-carousel-page-revisit');
  root.dataset.akNavigationMode = internal ? 'internal' : 'entry';


  // Only the real document owns its frame. Discard snapshots produced by older
  // versions so no serialized HTML can leak into a subsequent navigation.
  try {
    sessionStorage.removeItem('ak-frame-carry-v1');
    sessionStorage.removeItem('ak-frame-carry-v2');
  } catch {}
  function visible(selector) {
    return Array.from(document.querySelectorAll(selector)).find(element => {
      const box = element.getBoundingClientRect();
      return box.width > 0 && box.height > 0 && getComputedStyle(element).display !== 'none';
    });
  }
  function frameParts() {
    return [
      ['top', visible('.ak-single-level-menu') || visible('[data-ak-inner-brand]') ||
        visible('.ak-top-front-anchor .ak-grid-section-2x-reverse')],
      ['bottom', visible('.ak-bottom-nav')],
      ['left', visible('.ak-site-frame-rail--left')],
      ['right', visible('.ak-site-frame-rail--right')]
    ];
  }
  function nameFrame() {
    document.querySelectorAll('[data-ak-navigation-frame]').forEach(element => {
      element.style.removeProperty('view-transition-name');
      element.removeAttribute('data-ak-navigation-frame');
    });
    const parts = frameParts();
    parts.forEach(([name, element]) => {
      if (!element) return;
      element.dataset.akNavigationFrame = name;
      // Keep the frame in the root snapshot with its universal masks and
      // mobile stacking contexts. Extracting individual bars loses that order.
    });
    const frameReady = document.readyState !== 'loading' &&
      (!document.fonts || document.fonts.status === 'loaded') && parts.every(([, element]) => element);
    root.classList.toggle('ak-navigation-frame-ready', frameReady);
    // On a slow internal navigation, feedback occupies the content opening;
    // the fixed frame stays visible around it.
    if (frameReady && (internal || root.classList.contains('ak-navigation-leaving'))) {
      const layer = document.querySelector('.ak-boot-screen');
      const bounds = Object.fromEntries(parts.filter(([, element]) => element)
        .map(([name, element]) => [name, element.getBoundingClientRect()]));
      if (layer && bounds.top && bounds.bottom && bounds.left && bounds.right) {
        layer.style.clipPath = `inset(${Math.max(0,bounds.top.bottom)}px ${Math.max(0,innerWidth-bounds.right.left)}px ${Math.max(0,innerHeight-bounds.bottom.top)}px ${Math.max(0,bounds.left.right)}px)`;
      }
    }
  }
  function remember(url) {
    const next = route(url);
    if (!next || samePage(next, new URL(location.href)) || !root.classList.contains('ak-boot-revealed')) return;
    try { sessionStorage.setItem(key, JSON.stringify({from:location.href,to:next.href,at:Date.now()})); } catch {}
  }
  function leave(url, force = false) {
    const next = route(url);
    if (!next || (!force && samePage(next,new URL(location.href)))) return;
    const alreadyLeaving = root.classList.contains('ak-navigation-leaving');
    const layer = document.querySelector('.ak-boot-screen');
    if (layer && !alreadyLeaving) {
      // Every newly displayed loader begins at zero; no elapsed-time offset.
      window.AKLoaderMotion?.reset(layer);
      layer.setAttribute('aria-valuenow','0');
    }
    root.classList.add('ak-navigation-leaving');
    window.dispatchEvent(new CustomEvent('ak:navigation-start'));
    remember(next.href);
    nameFrame();
    if (layer) layer.setAttribute('aria-hidden','false');
    root.dataset.akLoaderState = 'leaving';
  }
  // Close overlays before the browser snapshots the outgoing page. The link
  // remains a normal link: modifiers, downloads and prevented clicks keep working.
  document.addEventListener('click', event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    // A microtask can run between native event listeners, before a later
    // pagination/filter handler cancels this click. Wait until the entire
    // dispatch has finished before deciding that a real navigation will occur.
    setTimeout(() => { if (!event.defaultPrevented) leave(link.href); }, 0);
  });
  window.addEventListener('pageswap', event => {
    const destination = event.activation?.entry?.url;
    if (destination) leave(destination);
    nameFrame();
  });
  function ready() {
    nameFrame();
    root.classList.remove('ak-navigation-leaving');
    root.classList.add('ak-navigation-ready');
    root.dataset.akNavigationPhase = 'complete';
    root.dataset.akLoaderState = 'ready';
    const layer = document.querySelector('.ak-boot-screen');
    if (layer) {
      layer.style.setProperty('--ak-load-progress',100);
      layer.setAttribute('aria-valuenow','100');
      layer.setAttribute('aria-hidden','true');
    }
  }
  window.addEventListener('ak:boot-revealed', ready);
  window.addEventListener('pagereveal', event => {
    root.classList.remove('ak-navigation-leaving');
    if (event.viewTransition) {
      event.viewTransition.ready.catch(() => {});
      event.viewTransition.skipTransition();
    }
    nameFrame();
    if (root.classList.contains('ak-boot-revealed')) ready();
    else root.dataset.akLoaderState = 'preparing';
  });
  document.addEventListener('DOMContentLoaded', () => {
    nameFrame();
    document.fonts?.ready.then(nameFrame);
  }, {once:true});
  window.addEventListener('load', nameFrame, {once:true});
  window.addEventListener('ak:prepare-page-reveal', nameFrame);
  window.addEventListener('resize', () => { nameFrame(); }, {passive:true});
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    // BFCache already restored the real DOM, choices, slider and scroll.
    // Remove the outgoing loader without replacing or replaying that page.
    ready();
  });
})();
